import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../services/api';
import { resolveBlogImageUrl } from '../utils/tenantAssetUrls';
import './BlogPost.css';

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchPost() {
      try {
        const response = await api.get(`/blog/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Post not found');
          }
          throw new Error('Failed to fetch blog post');
        }
        const data = await response.json();
        setPost(data.data);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  /** Strip Squarespace layout chrome that creates huge empty gaps */
  const sanitizePostHtml = (html) => {
    if (!html) return '';
    if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
      return String(html)
        .replace(/padding-bottom\s*:\s*[\d.]+%;?/gi, 'padding-bottom:0;')
        .replace(/<p[^>]*>\s*(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*\s*<\/p>/gi, '');
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Kill aspect-ratio spacer hacks (inline padding-bottom on image wrappers)
    doc.querySelectorAll('[style*="padding-bottom"], .has-aspect-ratio, .intrinsic, .content-fit').forEach((el) => {
      el.style.paddingBottom = '0';
      el.style.height = 'auto';
      el.style.minHeight = '0';
      el.style.position = 'static';
      el.style.aspectRatio = 'auto';
    });

    // Prefer data-src images; normalize img styles
    doc.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('data-src') || img.getAttribute('data-image') || img.getAttribute('src');
      if (src) img.setAttribute('src', src);
      img.removeAttribute('style');
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.style.position = 'static';
    });

    // Drop empty paragraphs / whitespace-only nodes
    doc.querySelectorAll('p').forEach((p) => {
      const text = (p.textContent || '').replace(/\u00a0/g, ' ').trim();
      if (!text && !p.querySelector('img')) p.remove();
    });

    return doc.body.innerHTML;
  };

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading post...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-page">
        <div className="container">
          <div className="error-state">
            <h2>Post Not Found</h2>
            <p>{error || 'This blog post could not be found.'}</p>
            <Link to="/blog" className="btn btn-primary">
              ← Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      {/* Hero with featured image */}
      {post.featured_image && (
        <section className="post-hero">
          <div className="post-hero-image">
            <img src={resolveBlogImageUrl(post.featured_image)} alt={post.title} />
          </div>
        </section>
      )}

      <article className="post-content section">
        <div className="container container-narrow">
          {/* Back link */}
          <Link to="/blog" className="back-link">
            ← Back to Blog
          </Link>

          {/* Post header */}
          <header className="post-header">
            <h1>{post.title}</h1>
            <div className="post-meta">
              {post.author_name && (
                <span className="post-author">By {post.author_name}</span>
              )}
              {post.published_at && (
                <span className="post-date">{formatDate(post.published_at)}</span>
              )}
            </div>
            {post.tags && post.tags.length > 0 && (
              <div className="post-tags">
                {post.tags.map((tag, index) => (
                  <Link key={index} to={`/blog?tag=${tag}`} className="post-tag">
                    {tag}
                  </Link>
                ))}
              </div>
            )}
          </header>

          {/* Post body */}
          <div 
            className="post-body"
            dangerouslySetInnerHTML={{ __html: sanitizePostHtml(post.content) }}
          />

          {/* Share / Navigation */}
          <footer className="post-footer">
            <div className="share-section">
              <span>Share this post:</span>
              <div className="share-buttons">
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn facebook"
                >
                  Facebook
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn twitter"
                >
                  Twitter
                </a>
                <a 
                  href={`mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(window.location.href)}`}
                  className="share-btn email"
                >
                  Email
                </a>
              </div>
            </div>

            <Link to="/blog" className="btn btn-secondary">
              ← Back to All Posts
            </Link>
          </footer>
        </div>
      </article>
    </div>
  );
}

export default BlogPost;
